/**
 * OccupancyEditor — the one pop-up for entering a night's in-house figures.
 * Used from Home, the function sheets and the daily sheet screen view.
 */
import { useState } from 'react';
import { BedDouble, Users, X, Check, AlertTriangle, Trash2 } from 'lucide-react';
import { saveOccupancy, clearOccupancy, occWarning } from '../lib/occupancy';
import { parseLocalDate } from '../lib/dateHelpers';

export default function OccupancyEditor({ date, current, onClose }) {
  const [rooms, setRooms] = useState(current ? String(current.rooms) : '');
  const [guests, setGuests] = useState(current ? String(current.guests) : '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const warn = occWarning(rooms, guests);
  const label = parseLocalDate(date)?.toLocaleDateString('en-IE', { weekday: 'long', day: 'numeric', month: 'long' });
  const digits = (v) => v.replace(/[^0-9]/g, '').slice(0, 3);

  const save = async () => {
    if (rooms === '' && guests === '') return;
    setSaving(true); setErr('');
    try { await saveOccupancy(date, rooms, guests); onClose(); }
    catch (e) { console.error('[occupancy] save failed', e); setErr('Could not save — please try again'); }
    finally { setSaving(false); }
  };
  const clear = async () => {
    setSaving(true); setErr('');
    try { await clearOccupancy(date); onClose(); }
    catch (e) { console.error('[occupancy] clear failed', e); setErr('Could not clear — please try again'); }
    finally { setSaving(false); }
  };
  const field = 'w-full text-3xl font-bold text-gray-900 border-2 border-gray-300 rounded-lg px-3 py-2 text-center tabular-nums focus:outline-none focus:border-sky-600';

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[90] p-4 print:hidden no-print" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-gray-100">
          <div>
            <p className="font-bold text-gray-900">In-house tonight</p>
            <p className="text-sm text-gray-500">{label}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 mb-1.5"><BedDouble size={15} />Rooms occupied</span>
              <input inputMode="numeric" autoFocus value={rooms} onChange={(e) => setRooms(digits(e.target.value))}
                onKeyDown={(e) => e.key === 'Enter' && save()} className={field} />
            </label>
            <label className="block">
              <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 mb-1.5"><Users size={15} />Guests in house</span>
              <input inputMode="numeric" value={guests} onChange={(e) => setGuests(digits(e.target.value))}
                onKeyDown={(e) => e.key === 'Enter' && save()} className={field} />
            </label>
          </div>
          {warn && <p className="flex items-start gap-1.5 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"><AlertTriangle size={15} className="mt-0.5 shrink-0" />{warn}</p>}
          {err && <p className="text-sm text-red-600">{err}</p>}
          <div className="flex gap-2">
            {current && (
              <button onClick={clear} disabled={saving} title="Remove these figures" className="px-3 py-2 text-sm border border-gray-200 rounded-lg text-gray-500 hover:text-red-600 hover:border-red-200"><Trash2 size={15} /></button>
            )}
            <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50">Cancel</button>
            <button onClick={save} disabled={saving || (rooms === '' && guests === '')}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 text-sm bg-sky-700 text-white rounded-lg font-semibold hover:bg-sky-800 disabled:opacity-40">
              <Check size={15} />{saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
